
import React from 'react';
import { ChevronLeft, Layers, Download, Upload, Calendar } from 'lucide-react';
import { ReportMetadata, ProjectGlobalData, ProjectStatus } from '../../types';
import { SIFA_STATUS_LABELS } from './sifaBetriebsdatenLogic';
import { KontextHilfe } from '../lernzentrum/KontextHilfe';
import { useLernzentrumOeffner } from '../lernzentrum/lernzentrumOeffner';
import type { TutorialKapitelId } from '../../tutorial/types';

const StatusBadge = ({ status, sifa }: { status: ProjectStatus; sifa: boolean }) => {
  const styles = {
      planning: "bg-blue-100 text-blue-700 border-blue-200",
      // SiFa: Betreuung in Teal — die Farbe der SiFa-Kacheln, nicht das
      // Ausfuehrungs-Gruen einer Baustelle.
      active: sifa ? "bg-teal-100 text-teal-700 border-teal-200" : "bg-emerald-100 text-emerald-700 border-emerald-200",
      on_hold: "bg-orange-100 text-orange-700 border-orange-200",
      completed: "bg-purple-100 text-purple-700 border-purple-200",
      finished: "bg-slate-100 text-slate-700 border-slate-200"
  };
  const labels = sifa ? SIFA_STATUS_LABELS : {
      planning: "Planung",
      active: "Ausführung",
      on_hold: "Pausiert",
      completed: "Beendet",
      finished: "Archiv"
  };
  return (
      <span className={`px-3 py-1 rounded-full text-[11px] font-bold border uppercase tracking-wider shadow-sm ${styles[status]}`}>
          {labels[status]}
      </span>
  );
};

interface Props {
  metadata: ReportMetadata;
  globalData: ProjectGlobalData;
  activeSectionId: string | null;
  sections: any[];
  isPdfImporting: boolean;
  onBack: () => void;
  onDownloadIcs: () => void;
  onDownloadJson: () => void;
  onImportJsonClick: () => void;
  onImportPdfClick: () => void;
  /**
   * Kontext-"?" rechts im Kopf (Lernzentrum). Optional und mit Rueckfall auf
   * den Kontext aus App.tsx: `ProjectHub` reicht die Prop heute nicht durch
   * (fremder Auftrag), der Kontext liefert denselben Rueckruf.
   */
  onOeffneLernzentrum?: (kapitelId?: TutorialKapitelId) => void;
}

export const ProjectHeader: React.FC<Props> = ({
  metadata,
  globalData,
  activeSectionId,
  sections,
  onBack,
  onDownloadIcs,
  onDownloadJson,
  onImportJsonClick,
  onOeffneLernzentrum
}) => {

  // Prop sticht Kontext: wer den Header direkt versorgt, bestimmt den Weg.
  const oeffneAusKontext = useLernzentrumOeffner();
  const oeffneLernzentrum = onOeffneLernzentrum ?? oeffneAusKontext;

  const getSectionName = (id: string) => {
    const found = sections.find(s => s.id === id);
    return found ? found.name : 'Unbekannt';
  };
  // SiFa-Betreuung: andere Status-Beschriftung, kein ICS-Kalender (der
  // exportiert Begehungstermine aus dem Turnus, den es hier nicht gibt).
  const istSifa = metadata.projectType === 'sifa';

  return (
    <div data-tutorial="hub.kopf" data-glass="kopf" className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/80 p-4 flex flex-col md:flex-row justify-between items-start md:items-center shadow-sm gap-4 transition-all duration-300">
        <div className="flex items-center gap-4">
            <button 
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    onBack();
                }} 
                className="p-2.5 bg-white hover:bg-slate-100 border border-slate-200 hover:border-slate-300 rounded-xl transition-all text-slate-500 shadow-sm group active:scale-95"
                title="Zurück zum Dashboard"
            >
                <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div>
                <h1 className="text-xl font-black text-slate-800 tracking-tight leading-none">Projekt-Zentrale</h1>
                <div className="flex items-center gap-2 text-xs text-slate-500 font-medium mt-1">
                    <span className="truncate max-w-[200px] md:max-w-md">{metadata.projectName || 'Neues Projekt'}</span>
                    {metadata.projectNumber && (
                        <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded font-mono text-sm font-bold border border-slate-200">#{metadata.projectNumber}</span>
                    )}
                    {activeSectionId && activeSectionId !== 'all' && (
                        <>
                            <span className="text-slate-300">|</span>
                            <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100 flex items-center gap-1 text-[11px]">
                                <Layers className="w-3 h-3" /> {getSectionName(activeSectionId)}
                            </span>
                        </>
                    )}
                </div>
            </div>
        </div>
        
        <div className="flex items-center gap-3 self-end md:self-auto relative">
            <div className="hidden md:block">
                <StatusBadge status={globalData.status} sifa={istSifa} />
            </div>

            <div className="h-6 w-px bg-slate-200 mx-1 hidden md:block"></div>

            <div className="flex bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
                 {!istSifa && (
                 <>
                 <button onClick={onDownloadIcs} className="p-2 hover:bg-slate-50 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Kalender (ICS)">
                    <Calendar className="w-4 h-4" />
                 </button>
                 <div className="w-px bg-slate-100 my-1"></div>
                 </>
                 )}
                 <button onClick={onImportJsonClick} className="p-2 hover:bg-slate-50 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Laden (JSON)">
                    <Upload className="w-4 h-4" />
                 </button>
                 <button onClick={onDownloadJson} className="p-2 hover:bg-slate-50 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Speichern (JSON)">
                    <Download className="w-4 h-4" />
                 </button>
            </div>

            {/* Kontext-"?" ganz rechts im Kopf. In der Projekt-Zentrale
                blendet App.tsx dafuer das schwebende Fragezeichen aus. */}
            <KontextHilfe view="project-hub" onOeffneLernzentrum={oeffneLernzentrum} variant="inline" />
        </div>
    </div>
  );
};
